import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, X, ChevronDown, ArrowRight,
  Calendar, CreditCard, Globe, Users, Ship,
  BarChart3, Bell, Smartphone, Clock,
  Shield, Zap, CalendarCheck, Wallet, UserCheck, Share2, Anchor
} from "lucide-react";
import { FadeInUp } from "./components/visual/CountUp";
import { CommissionCalculator } from "./components/visual/CommissionCalculator";
import { CustomCursor } from "./components/visual/CustomCursor";
import { StickyHelpButton } from "./components/StickyHelpButton";
import { usePageMeta } from "./lib/usePageMeta";
import { usePageT } from "./i18n/LanguageContext";
import { LanguageSwitcher } from "./components/LanguageSwitcher";

const translations = {
  it: {
    metaTitle: "Nautiqo — Gestisci il tuo charter senza intermediari",
    metaDesc: "Prenotazioni, disponibilita e pagamenti per charter nautici. Solo 3% di commissioni. Setup in 5 minuti, 30 giorni gratuiti.",
    navWhy: "Perche Nautiqo",
    navFeatures: "Funzionalita",
    navPricing: "Prezzi",
    navHow: "Come funziona",
    navFaq: "FAQ",
    navLogin: "Accedi",
    navCta: "Inizia gratis",
    heroTitle1: "Gestisci il tuo charter.",
    heroTitle2: "Senza intermediari.",
    heroSubtitle: "Prenotazioni, disponibilita e pagamenti. Tutto sotto il tuo controllo.",
    heroCtaPrimary: "Inizia gratis",
    heroCtaSecondary: "Accedi al pannello",
    heroBadge1: "Registrazione gratuita",
    heroBadge2: "Setup in 5 minuti",
    heroBadge3: "Primo mese gratis via App Store",
    diffLabel: "La differenza",
    diffTitle: "Perche Nautiqo e diverso",
    diffSubtitle: "Non e un marketplace. E il tuo sistema di gestione.",
    diffMarketplace: "Marketplace",
    diffFooterA: "Con Nautiqo",
    diffFooterAStrong: "costruisci un business",
    diffFooterB: "Con le altre piattaforme",
    diffFooterBSpan: "lavori per loro",
    compClienti: "Clienti",
    compClientiN: "I clienti sono tuoi",
    compClientiO: "I clienti sono della piattaforma",
    compCommissioni: "Commissioni",
    compCommissioniN: "3% trasparente",
    compCommissioniO: "Fino al 30%",
    compIncassi: "Incassi",
    compIncassiN: "Diretti sul tuo conto",
    compIncassiO: "Ritardati",
    compBranding: "Branding",
    compBrandingN: "Il tuo brand",
    compBrandingO: "Il loro brand",
    compFlotta: "Gestione flotta",
    compFlottaN: "Completa",
    compFlottaO: "Limitata",
    compMultiday: "Multi-day",
    compMultidayN: "Incluso",
    compMultidayO: "Non disponibile",
    compPrezzi: "Prezzi",
    compPrezziN: "Controllo totale",
    compPrezziO: "Limitato",
    bookingsLabel: "Prenotazioni",
    bookingsTitle: "Ogni prenotazione sotto controllo",
    bookingsItem1: "Richieste in tempo reale con notifiche push",
    bookingsItem2: "Conferma o rifiuta con un click",
    bookingsItem3: "Gestione acconti e saldi separati",
    bookingsItem4: "Lista, calendario e richieste in un'unica schermata",
    availLabel: "Disponibilita",
    availTitle: "Mai piu doppie prenotazioni",
    availItem1: "Calendario mensile, settimanale e giornaliero",
    availItem2: "Sincronizzazione iCal con Google Calendar e Airbnb",
    availItem3: "Blocco date per manutenzione",
    availItem4: "Disponibilita sempre aggiornata in tempo reale",
    fleetLabel: "Flotta",
    fleetTitle: "La tua flotta, sempre organizzata",
    fleetItem1: "Aggiungi barche con foto e listino prezzi",
    fleetItem2: "Tour in gruppo, esclusivi e noleggio multi-giorno",
    fleetItem3: "Prezzi stagionali per persona, mezza giornata o giornata intera",
    fleetItem4: "Attiva o disattiva ogni barca con un click",
    gridLabel: "Funzionalita",
    gridTitle: "Tutto in un'unica piattaforma",
    gf1Title: "Gestione flotta", gf1Desc: "Tutte le barche da un'unica dashboard",
    gf2Title: "Prenotazioni online 24/7", gf2Desc: "Link dedicato, sempre attivo",
    gf3Title: "Conferme e richieste", gf3Desc: "Lista, calendario e richieste nello stesso tab",
    gf4Title: "Tour condivisi ed esclusivi", gf4Desc: "Gruppi, esclusiva e noleggio multi-giorno",
    gf5Title: "Calendario disponibilita", gf5Desc: "Mensile, settimanale e giornaliero",
    gf6Title: "Pagamenti Stripe", gf6Desc: "Incassi diretti, acconti e saldi separati",
    gf7Title: "Riepilogo giornaliero", gf7Desc: "Incassato e da incassare ogni giorno",
    gf8Title: "Acconti e saldi", gf8Desc: "Pagamenti parziali e finali",
    gf9Title: "Notifiche real-time", gf9Desc: "Alert push per nuove prenotazioni",
    gf10Title: "Meteo integrato", gf10Desc: "Previsioni per porto per i prossimi 3 giorni",
    gf11Title: "Gestione staff", gf11Desc: "Invita collaboratori con ruoli Manager o Staff",
    gf12Title: "Link prenotazione", gf12Desc: "Condividi su WhatsApp, social e Google",
    whyTitle: "Perche scegliere Nautiqo",
    whySubtitle: "Il controllo totale sulla tua attivita.",
    why1Title: "I clienti restano tuoi", why1Desc: "Nessun intermediario tra te e i tuoi clienti",
    why2Title: "Incassi sul tuo conto", why2Desc: "Pagamenti diretti via Stripe",
    why3Title: "Solo 3% di commissione", why3Desc: "Trasparente. Il 97% resta a te.",
    why4Title: "Meno lavoro manuale", why4Desc: "Automatizza prenotazioni e conferme",
    why5Title: "Tutto in una piattaforma", why5Desc: "Niente piu Excel o fogli sparsi",
    why6Title: "Pensato per chi naviga", why6Desc: "Progettato per charter e tour nautici",
    howLabel: "Come funziona",
    howTitle: "Tre semplici passi",
    how1Title: "Configura la flotta", how1Desc: "Aggiungi barche, prezzi e disponibilita.",
    how2Title: "Ricevi prenotazioni", how2Desc: "Condividi il link. I clienti prenotano 24/7.",
    how3Title: "Gestisci tutto", how3Desc: "Conferma, incassa e monitora da un'unica dashboard.",
    pricingLabel: "Abbonamento",
    pricingTitle: "Un piano. Tutto incluso.",
    pricingSubtitle: "Prova gratis per 30 giorni. Poi scegli la frequenza che preferisci.",
    trialTitle: "Primo mese gratis",
    trialSub: "Trial via App Store",
    trialPrice: "Gratis",
    trialPer: "/ 30 giorni",
    trialF1: "Tutte le funzionalita",
    trialF2: "Barche illimitate",
    trialF3: "Attivato dall'app, non dalla carta",
    trialCta: "Crea account",
    monthlyTitle: "Mensile",
    monthlySub: "Fatturazione mensile",
    monthlyPer: "/ mese",
    monthlyF1: "Tutto illimitato",
    monthlyF2: "Cancella quando vuoi",
    monthlyF3: "Pagamenti, staff, report",
    monthlyCta: "Scegli mensile",
    annualBadge: "Risparmia 17%",
    annualTitle: "Annuale",
    annualSub: "Fatturazione annuale",
    annualPer: "/ anno",
    annualNote: "€16,58/mese — Risparmi €40 all'anno",
    annualF1: "Tutto illimitato",
    annualF2: "Stesse funzionalita del mensile",
    annualF3: "Miglior prezzo",
    annualCta: "Scegli annuale",
    pricingFooter: "Cancella quando vuoi. Nessun vincolo, nessun costo nascosto.",
    downloadLabel: "Scarica l'app",
    downloadTitle: "Gestisci tutto dal tuo smartphone",
    downloadDesc: "Scarica Nautiqo e inizia a gestire prenotazioni, disponibilita e pagamenti ovunque ti trovi.",
    downloadFor: "Scarica per",
    finalTitle1: "Inizia a gestire il tuo charter",
    finalTitle2: "senza intermediari",
    finalSubtitle: "Configura Nautiqo in pochi minuti e prendi il controllo del tuo business.",
    finalCtaPrimary: "Inizia gratis",
    finalWhatsapp: "Contattaci su WhatsApp",
    faqLabel: "FAQ",
    faqTitle: "Domande frequenti",
    footerDesc: "La piattaforma per gestire prenotazioni, disponibilita e pagamenti del tuo charter. Senza intermediari.",
    footerProduct: "Prodotto",
    footerAccount: "Account",
    footerContacts: "Contatti",
    footerCompany1: "è un servizio di",
    footerRights: "© 2026 Nautiqo. Tutti i diritti riservati.",
    footerPrivacy: "Privacy Policy",
    footerTerms: "Termini di servizio",
    faq1Q: "Posso usare Nautiqo senza un sito web?",
    faq1A: "Sì. Ricevi un link di prenotazione dedicato che puoi condividere su social, WhatsApp, email o Google Business. I clienti prenotano 24/7 senza bisogno di un sito.",
    faq2Q: "Come funzionano i pagamenti e i rimborsi?",
    faq2A: "I pagamenti vengono elaborati tramite Stripe Connect direttamente sul tuo conto bancario. Puoi richiedere un acconto o il pagamento completo. I fondi arrivano in 2-7 giorni lavorativi. Per i rimborsi puoi emetterli direttamente dall'app, in modo totale o parziale, e il cliente riceve una notifica email automatica.",
    faq3Q: "Posso gestire tour condivisi, esclusivi e noleggi?",
    faq3A: "Sì, puoi abilitare più modalità sulla stessa barca: Tour in Gruppo (prezzo a persona), Tour Esclusiva (barca intera) e Noleggio (prezzo fisso giornaliero). Ognuna ha prezzi e stagionalità indipendenti.",
    faq4Q: "Sono supportate le prenotazioni multi-giorno?",
    faq4A: "Sì. Per le barche in modalità Noleggio puoi abilitare prenotazioni di più giorni con skipper opzionale o obbligatorio. Il prezzo viene calcolato automaticamente in base alla durata.",
    faq5Q: "Posso collegare il mio sito web?",
    faq5A: "Assolutamente. Puoi inserire il tuo link di prenotazione in qualsiasi sito (WordPress, Wix, Squarespace), nella bio di Instagram o come pulsante WhatsApp. Setup in meno di un minuto, zero installazioni.",
    faq6Q: "Posso gestire un team di collaboratori?",
    faq6A: "Sì. Puoi invitare collaboratori via email e assegnare il ruolo Manager (accesso completo) o Staff (solo visualizzazione). Ognuno accede con le proprie credenziali.",
    faq7Q: "Posso sincronizzare calendari esterni?",
    faq7A: "Sì, Nautiqo supporta la sincronizzazione iCal con Google Calendar, Airbnb, Booking.com e altri sistemi che supportano il formato standard.",
    faq8Q: "Come funziona il periodo di prova?",
    faq8A: "Crei un account gratuito sul sito, scarichi l'app e attivi il tuo abbonamento direttamente da App Store. Il primo mese è gratuito — gestito da Apple, senza addebiti fino alla fine del trial. Poi scegli mensile o annuale.",
  },
  en: {
    metaTitle: "Nautiqo — Manage your charter without middlemen",
    metaDesc: "Bookings, availability and payments for boat charters. Just 3% commission. Set up in 5 minutes, 30 days free.",
    navWhy: "Why Nautiqo",
    navFeatures: "Features",
    navPricing: "Pricing",
    navHow: "How it works",
    navFaq: "FAQ",
    navLogin: "Log in",
    navCta: "Start free",
    heroTitle1: "Manage your charter.",
    heroTitle2: "Without middlemen.",
    heroSubtitle: "Bookings, availability and payments. All under your control.",
    heroCtaPrimary: "Start free",
    heroCtaSecondary: "Go to dashboard",
    heroBadge1: "Free sign-up",
    heroBadge2: "Set up in 5 minutes",
    heroBadge3: "First month free via App Store",
    diffLabel: "The difference",
    diffTitle: "Why Nautiqo is different",
    diffSubtitle: "It's not a marketplace. It's your management system.",
    diffMarketplace: "Marketplace",
    diffFooterA: "With Nautiqo you",
    diffFooterAStrong: "build a business",
    diffFooterB: "With other platforms you",
    diffFooterBSpan: "work for them",
    compClienti: "Customers",
    compClientiN: "Your customers are yours",
    compClientiO: "Customers belong to the platform",
    compCommissioni: "Commission",
    compCommissioniN: "3% transparent",
    compCommissioniO: "Up to 30%",
    compIncassi: "Payouts",
    compIncassiN: "Straight to your account",
    compIncassiO: "Delayed",
    compBranding: "Branding",
    compBrandingN: "Your brand",
    compBrandingO: "Their brand",
    compFlotta: "Fleet management",
    compFlottaN: "Complete",
    compFlottaO: "Limited",
    compMultiday: "Multi-day",
    compMultidayN: "Included",
    compMultidayO: "Not available",
    compPrezzi: "Pricing",
    compPrezziN: "Full control",
    compPrezziO: "Limited",
    bookingsLabel: "Bookings",
    bookingsTitle: "Every booking under control",
    bookingsItem1: "Real-time requests with push notifications",
    bookingsItem2: "Confirm or decline in one click",
    bookingsItem3: "Separate deposit and balance management",
    bookingsItem4: "List, calendar and requests on a single screen",
    availLabel: "Availability",
    availTitle: "No more double bookings",
    availItem1: "Monthly, weekly and daily calendar",
    availItem2: "iCal sync with Google Calendar and Airbnb",
    availItem3: "Block dates for maintenance",
    availItem4: "Availability always updated in real time",
    fleetLabel: "Fleet",
    fleetTitle: "Your fleet, always organized",
    fleetItem1: "Add boats with photos and price lists",
    fleetItem2: "Group tours, exclusive tours and multi-day rentals",
    fleetItem3: "Seasonal pricing per person, half day or full day",
    fleetItem4: "Activate or deactivate any boat in one click",
    gridLabel: "Features",
    gridTitle: "Everything in one platform",
    gf1Title: "Fleet management", gf1Desc: "All your boats from one dashboard",
    gf2Title: "Online bookings 24/7", gf2Desc: "Dedicated link, always live",
    gf3Title: "Confirmations and requests", gf3Desc: "List, calendar and requests in one tab",
    gf4Title: "Shared and exclusive tours", gf4Desc: "Groups, exclusive and multi-day rentals",
    gf5Title: "Availability calendar", gf5Desc: "Monthly, weekly and daily",
    gf6Title: "Stripe payments", gf6Desc: "Direct payouts, separate deposits and balances",
    gf7Title: "Daily summary", gf7Desc: "Collected and outstanding amounts every day",
    gf8Title: "Deposits and balances", gf8Desc: "Partial and final payments",
    gf9Title: "Real-time notifications", gf9Desc: "Push alerts for new bookings",
    gf10Title: "Built-in weather", gf10Desc: "Per-port forecasts for the next 3 days",
    gf11Title: "Staff management", gf11Desc: "Invite collaborators with Manager or Staff roles",
    gf12Title: "Booking link", gf12Desc: "Share on WhatsApp, social and Google",
    whyTitle: "Why choose Nautiqo",
    whySubtitle: "Full control over your business.",
    why1Title: "Your customers stay yours", why1Desc: "No middleman between you and your customers",
    why2Title: "Payouts to your account", why2Desc: "Direct payments via Stripe",
    why3Title: "Only 3% commission", why3Desc: "Transparent. You keep 97%.",
    why4Title: "Less manual work", why4Desc: "Automate bookings and confirmations",
    why5Title: "All in one platform", why5Desc: "No more Excel or scattered sheets",
    why6Title: "Made for those who sail", why6Desc: "Designed for charters and boat tours",
    howLabel: "How it works",
    howTitle: "Three simple steps",
    how1Title: "Set up your fleet", how1Desc: "Add boats, prices and availability.",
    how2Title: "Receive bookings", how2Desc: "Share the link. Customers book 24/7.",
    how3Title: "Manage everything", how3Desc: "Confirm, collect and track from one dashboard.",
    pricingLabel: "Subscription",
    pricingTitle: "One plan. Everything included.",
    pricingSubtitle: "Try it free for 30 days. Then pick the billing that suits you.",
    trialTitle: "First month free",
    trialSub: "Trial via App Store",
    trialPrice: "Free",
    trialPer: "/ 30 days",
    trialF1: "All features",
    trialF2: "Unlimited boats",
    trialF3: "Activated from the app, not the card",
    trialCta: "Create account",
    monthlyTitle: "Monthly",
    monthlySub: "Monthly billing",
    monthlyPer: "/ month",
    monthlyF1: "Everything unlimited",
    monthlyF2: "Cancel anytime",
    monthlyF3: "Payments, staff, reports",
    monthlyCta: "Choose monthly",
    annualBadge: "Save 17%",
    annualTitle: "Annual",
    annualSub: "Annual billing",
    annualPer: "/ year",
    annualNote: "€16.58/month — Save €40 a year",
    annualF1: "Everything unlimited",
    annualF2: "Same features as monthly",
    annualF3: "Best price",
    annualCta: "Choose annual",
    pricingFooter: "Cancel anytime. No lock-in, no hidden costs.",
    downloadLabel: "Download the app",
    downloadTitle: "Manage everything from your smartphone",
    downloadDesc: "Download Nautiqo and start managing bookings, availability and payments wherever you are.",
    downloadFor: "Download for",
    finalTitle1: "Start managing your charter",
    finalTitle2: "without middlemen",
    finalSubtitle: "Set up Nautiqo in minutes and take control of your business.",
    finalCtaPrimary: "Start free",
    finalWhatsapp: "Contact us on WhatsApp",
    faqLabel: "FAQ",
    faqTitle: "Frequently asked questions",
    footerDesc: "The platform to manage bookings, availability and payments for your charter. Without middlemen.",
    footerProduct: "Product",
    footerAccount: "Account",
    footerContacts: "Contact",
    footerCompany1: "is a service by",
    footerRights: "© 2026 Nautiqo. All rights reserved.",
    footerPrivacy: "Privacy Policy",
    footerTerms: "Terms of service",
    faq1Q: "Can I use Nautiqo without a website?",
    faq1A: "Yes. You get a dedicated booking link you can share on social media, WhatsApp, email or Google Business. Customers book 24/7 with no website needed.",
    faq2Q: "How do payments and refunds work?",
    faq2A: "Payments are processed via Stripe Connect straight to your bank account. You can request a deposit or full payment. Funds arrive in 2-7 business days. Refunds can be issued directly from the app, in full or in part, and the customer receives an automatic email notification.",
    faq3Q: "Can I manage shared tours, exclusive tours and rentals?",
    faq3A: "Yes, you can enable multiple modes on the same boat: Group Tour (price per person), Exclusive Tour (whole boat) and Rental (fixed daily price). Each has independent pricing and seasonality.",
    faq4Q: "Are multi-day bookings supported?",
    faq4A: "Yes. For boats in Rental mode you can enable multi-day bookings with optional or mandatory skipper. The price is calculated automatically based on duration.",
    faq5Q: "Can I connect my website?",
    faq5A: "Absolutely. You can add your booking link to any website (WordPress, Wix, Squarespace), in your Instagram bio or as a WhatsApp button. Set up in under a minute, no installation.",
    faq6Q: "Can I manage a team of collaborators?",
    faq6A: "Yes. You can invite collaborators by email and assign the Manager role (full access) or Staff (view only). Each one logs in with their own credentials.",
    faq7Q: "Can I sync external calendars?",
    faq7A: "Yes, Nautiqo supports iCal sync with Google Calendar, Airbnb, Booking.com and other systems that support the standard format.",
    faq8Q: "How does the trial period work?",
    faq8A: "You create a free account on the website, download the app and activate your subscription directly from the App Store. The first month is free — managed by Apple, with no charge until the trial ends. Then you choose monthly or annual.",
  },
  es: {
    metaTitle: "Nautiqo — Gestiona tu charter sin intermediarios",
    metaDesc: "Reservas, disponibilidad y pagos para charters náuticos. Solo 3% de comisión. Configuración en 5 minutos, 30 días gratis.",
    navWhy: "Por qué Nautiqo",
    navFeatures: "Funciones",
    navPricing: "Precios",
    navHow: "Cómo funciona",
    navFaq: "FAQ",
    navLogin: "Acceder",
    navCta: "Empieza gratis",
    heroTitle1: "Gestiona tu charter.",
    heroTitle2: "Sin intermediarios.",
    heroSubtitle: "Reservas, disponibilidad y pagos. Todo bajo tu control.",
    heroCtaPrimary: "Empieza gratis",
    heroCtaSecondary: "Ir al panel",
    heroBadge1: "Registro gratuito",
    heroBadge2: "Configuración en 5 minutos",
    heroBadge3: "Primer mes gratis vía App Store",
    diffLabel: "La diferencia",
    diffTitle: "Por qué Nautiqo es diferente",
    diffSubtitle: "No es un marketplace. Es tu sistema de gestión.",
    diffMarketplace: "Marketplace",
    diffFooterA: "Con Nautiqo",
    diffFooterAStrong: "construyes un negocio",
    diffFooterB: "Con las otras plataformas",
    diffFooterBSpan: "trabajas para ellas",
    compClienti: "Clientes",
    compClientiN: "Tus clientes son tuyos",
    compClientiO: "Los clientes son de la plataforma",
    compCommissioni: "Comisiones",
    compCommissioniN: "3% transparente",
    compCommissioniO: "Hasta el 30%",
    compIncassi: "Cobros",
    compIncassiN: "Directos a tu cuenta",
    compIncassiO: "Retrasados",
    compBranding: "Marca",
    compBrandingN: "Tu marca",
    compBrandingO: "Su marca",
    compFlotta: "Gestión de flota",
    compFlottaN: "Completa",
    compFlottaO: "Limitada",
    compMultiday: "Multi-día",
    compMultidayN: "Incluido",
    compMultidayO: "No disponible",
    compPrezzi: "Precios",
    compPrezziN: "Control total",
    compPrezziO: "Limitado",
    bookingsLabel: "Reservas",
    bookingsTitle: "Cada reserva bajo control",
    bookingsItem1: "Solicitudes en tiempo real con notificaciones push",
    bookingsItem2: "Confirma o rechaza con un clic",
    bookingsItem3: "Gestión separada de señales y saldos",
    bookingsItem4: "Lista, calendario y solicitudes en una sola pantalla",
    availLabel: "Disponibilidad",
    availTitle: "Nunca más reservas duplicadas",
    availItem1: "Calendario mensual, semanal y diario",
    availItem2: "Sincronización iCal con Google Calendar y Airbnb",
    availItem3: "Bloqueo de fechas por mantenimiento",
    availItem4: "Disponibilidad siempre actualizada en tiempo real",
    fleetLabel: "Flota",
    fleetTitle: "Tu flota, siempre organizada",
    fleetItem1: "Añade barcos con fotos y lista de precios",
    fleetItem2: "Tours en grupo, exclusivos y alquiler multi-día",
    fleetItem3: "Precios de temporada por persona, media jornada o jornada completa",
    fleetItem4: "Activa o desactiva cada barco con un clic",
    gridLabel: "Funciones",
    gridTitle: "Todo en una sola plataforma",
    gf1Title: "Gestión de flota", gf1Desc: "Todos tus barcos desde un solo panel",
    gf2Title: "Reservas online 24/7", gf2Desc: "Enlace dedicado, siempre activo",
    gf3Title: "Confirmaciones y solicitudes", gf3Desc: "Lista, calendario y solicitudes en la misma pestaña",
    gf4Title: "Tours compartidos y exclusivos", gf4Desc: "Grupos, exclusiva y alquiler multi-día",
    gf5Title: "Calendario de disponibilidad", gf5Desc: "Mensual, semanal y diario",
    gf6Title: "Pagos con Stripe", gf6Desc: "Cobros directos, señales y saldos separados",
    gf7Title: "Resumen diario", gf7Desc: "Cobrado y por cobrar cada día",
    gf8Title: "Señales y saldos", gf8Desc: "Pagos parciales y finales",
    gf9Title: "Notificaciones en tiempo real", gf9Desc: "Avisos push para nuevas reservas",
    gf10Title: "Meteo integrado", gf10Desc: "Previsiones por puerto para los próximos 3 días",
    gf11Title: "Gestión de personal", gf11Desc: "Invita colaboradores con roles Manager o Staff",
    gf12Title: "Enlace de reserva", gf12Desc: "Comparte en WhatsApp, redes y Google",
    whyTitle: "Por qué elegir Nautiqo",
    whySubtitle: "El control total sobre tu negocio.",
    why1Title: "Tus clientes siguen siendo tuyos", why1Desc: "Sin intermediarios entre tú y tus clientes",
    why2Title: "Cobros en tu cuenta", why2Desc: "Pagos directos vía Stripe",
    why3Title: "Solo 3% de comisión", why3Desc: "Transparente. El 97% es para ti.",
    why4Title: "Menos trabajo manual", why4Desc: "Automatiza reservas y confirmaciones",
    why5Title: "Todo en una plataforma", why5Desc: "Se acabaron los Excel y hojas sueltas",
    why6Title: "Pensado para quien navega", why6Desc: "Diseñado para charters y tours náuticos",
    howLabel: "Cómo funciona",
    howTitle: "Tres simples pasos",
    how1Title: "Configura tu flota", how1Desc: "Añade barcos, precios y disponibilidad.",
    how2Title: "Recibe reservas", how2Desc: "Comparte el enlace. Los clientes reservan 24/7.",
    how3Title: "Gestiona todo", how3Desc: "Confirma, cobra y controla desde un solo panel.",
    pricingLabel: "Suscripción",
    pricingTitle: "Un plan. Todo incluido.",
    pricingSubtitle: "Prueba gratis 30 días. Luego elige la frecuencia que prefieras.",
    trialTitle: "Primer mes gratis",
    trialSub: "Prueba vía App Store",
    trialPrice: "Gratis",
    trialPer: "/ 30 días",
    trialF1: "Todas las funciones",
    trialF2: "Barcos ilimitados",
    trialF3: "Activado desde la app, no desde la tarjeta",
    trialCta: "Crear cuenta",
    monthlyTitle: "Mensual",
    monthlySub: "Facturación mensual",
    monthlyPer: "/ mes",
    monthlyF1: "Todo ilimitado",
    monthlyF2: "Cancela cuando quieras",
    monthlyF3: "Pagos, personal, informes",
    monthlyCta: "Elegir mensual",
    annualBadge: "Ahorra 17%",
    annualTitle: "Anual",
    annualSub: "Facturación anual",
    annualPer: "/ año",
    annualNote: "€16,58/mes — Ahorras €40 al año",
    annualF1: "Todo ilimitado",
    annualF2: "Mismas funciones que el mensual",
    annualF3: "Mejor precio",
    annualCta: "Elegir anual",
    pricingFooter: "Cancela cuando quieras. Sin compromiso, sin costes ocultos.",
    downloadLabel: "Descarga la app",
    downloadTitle: "Gestiona todo desde tu smartphone",
    downloadDesc: "Descarga Nautiqo y empieza a gestionar reservas, disponibilidad y pagos estés donde estés.",
    downloadFor: "Descargar para",
    finalTitle1: "Empieza a gestionar tu charter",
    finalTitle2: "sin intermediarios",
    finalSubtitle: "Configura Nautiqo en pocos minutos y toma el control de tu negocio.",
    finalCtaPrimary: "Empieza gratis",
    finalWhatsapp: "Contáctanos por WhatsApp",
    faqLabel: "FAQ",
    faqTitle: "Preguntas frecuentes",
    footerDesc: "La plataforma para gestionar reservas, disponibilidad y pagos de tu charter. Sin intermediarios.",
    footerProduct: "Producto",
    footerAccount: "Cuenta",
    footerContacts: "Contacto",
    footerCompany1: "es un servicio de",
    footerRights: "© 2026 Nautiqo. Todos los derechos reservados.",
    footerPrivacy: "Política de privacidad",
    footerTerms: "Términos del servicio",
    faq1Q: "¿Puedo usar Nautiqo sin una web?",
    faq1A: "Sí. Recibes un enlace de reserva dedicado que puedes compartir en redes sociales, WhatsApp, email o Google Business. Los clientes reservan 24/7 sin necesidad de una web.",
    faq2Q: "¿Cómo funcionan los pagos y los reembolsos?",
    faq2A: "Los pagos se procesan mediante Stripe Connect directamente en tu cuenta bancaria. Puedes solicitar una señal o el pago completo. Los fondos llegan en 2-7 días hábiles. Los reembolsos puedes emitirlos directamente desde la app, de forma total o parcial, y el cliente recibe una notificación por email automática.",
    faq3Q: "¿Puedo gestionar tours compartidos, exclusivos y alquileres?",
    faq3A: "Sí, puedes habilitar varias modalidades en el mismo barco: Tour en Grupo (precio por persona), Tour Exclusivo (barco entero) y Alquiler (precio fijo diario). Cada una tiene precios y estacionalidad independientes.",
    faq4Q: "¿Se admiten las reservas multi-día?",
    faq4A: "Sí. Para los barcos en modo Alquiler puedes habilitar reservas de varios días con patrón opcional u obligatorio. El precio se calcula automáticamente según la duración.",
    faq5Q: "¿Puedo conectar mi web?",
    faq5A: "Por supuesto. Puedes insertar tu enlace de reserva en cualquier web (WordPress, Wix, Squarespace), en la bio de Instagram o como botón de WhatsApp. Configuración en menos de un minuto, cero instalaciones.",
    faq6Q: "¿Puedo gestionar un equipo de colaboradores?",
    faq6A: "Sí. Puedes invitar a colaboradores por email y asignar el rol Manager (acceso completo) o Staff (solo visualización). Cada uno accede con sus propias credenciales.",
    faq7Q: "¿Puedo sincronizar calendarios externos?",
    faq7A: "Sí, Nautiqo admite la sincronización iCal con Google Calendar, Airbnb, Booking.com y otros sistemas que admiten el formato estándar.",
    faq8Q: "¿Cómo funciona el periodo de prueba?",
    faq8A: "Creas una cuenta gratuita en la web, descargas la app y activas tu suscripción directamente desde App Store. El primer mes es gratis — gestionado por Apple, sin cargos hasta el final de la prueba. Luego eliges mensual o anual.",
  },
  fr: {
    metaTitle: "Nautiqo — Gérez votre charter sans intermédiaires",
    metaDesc: "Réservations, disponibilités et paiements pour charters nautiques. Seulement 3% de commission. Installation en 5 minutes, 30 jours gratuits.",
    navWhy: "Pourquoi Nautiqo",
    navFeatures: "Fonctionnalités",
    navPricing: "Tarifs",
    navHow: "Comment ça marche",
    navFaq: "FAQ",
    navLogin: "Se connecter",
    navCta: "Commencer gratuitement",
    heroTitle1: "Gérez votre charter.",
    heroTitle2: "Sans intermédiaires.",
    heroSubtitle: "Réservations, disponibilités et paiements. Tout sous votre contrôle.",
    heroCtaPrimary: "Commencer gratuitement",
    heroCtaSecondary: "Accéder au tableau de bord",
    heroBadge1: "Inscription gratuite",
    heroBadge2: "Installation en 5 minutes",
    heroBadge3: "Premier mois gratuit via App Store",
    diffLabel: "La différence",
    diffTitle: "Pourquoi Nautiqo est différent",
    diffSubtitle: "Ce n'est pas une marketplace. C'est votre système de gestion.",
    diffMarketplace: "Marketplace",
    diffFooterA: "Avec Nautiqo, vous",
    diffFooterAStrong: "construisez une entreprise",
    diffFooterB: "Avec les autres plateformes, vous",
    diffFooterBSpan: "travaillez pour elles",
    compClienti: "Clients",
    compClientiN: "Vos clients vous appartiennent",
    compClientiO: "Les clients appartiennent à la plateforme",
    compCommissioni: "Commissions",
    compCommissioniN: "3% transparent",
    compCommissioniO: "Jusqu'à 30%",
    compIncassi: "Encaissements",
    compIncassiN: "Directs sur votre compte",
    compIncassiO: "Retardés",
    compBranding: "Image de marque",
    compBrandingN: "Votre marque",
    compBrandingO: "Leur marque",
    compFlotta: "Gestion de flotte",
    compFlottaN: "Complète",
    compFlottaO: "Limitée",
    compMultiday: "Multi-jours",
    compMultidayN: "Inclus",
    compMultidayO: "Non disponible",
    compPrezzi: "Tarifs",
    compPrezziN: "Contrôle total",
    compPrezziO: "Limité",
    bookingsLabel: "Réservations",
    bookingsTitle: "Chaque réservation sous contrôle",
    bookingsItem1: "Demandes en temps réel avec notifications push",
    bookingsItem2: "Confirmez ou refusez en un clic",
    bookingsItem3: "Gestion séparée des acomptes et des soldes",
    bookingsItem4: "Liste, calendrier et demandes sur un seul écran",
    availLabel: "Disponibilités",
    availTitle: "Fini les doubles réservations",
    availItem1: "Calendrier mensuel, hebdomadaire et journalier",
    availItem2: "Synchronisation iCal avec Google Calendar et Airbnb",
    availItem3: "Blocage de dates pour la maintenance",
    availItem4: "Disponibilité toujours à jour en temps réel",
    fleetLabel: "Flotte",
    fleetTitle: "Votre flotte, toujours organisée",
    fleetItem1: "Ajoutez des bateaux avec photos et grille tarifaire",
    fleetItem2: "Tours en groupe, exclusifs et location multi-jours",
    fleetItem3: "Tarifs saisonniers par personne, demi-journée ou journée entière",
    fleetItem4: "Activez ou désactivez chaque bateau en un clic",
    gridLabel: "Fonctionnalités",
    gridTitle: "Tout sur une seule plateforme",
    gf1Title: "Gestion de flotte", gf1Desc: "Tous vos bateaux depuis un seul tableau de bord",
    gf2Title: "Réservations en ligne 24/7", gf2Desc: "Lien dédié, toujours actif",
    gf3Title: "Confirmations et demandes", gf3Desc: "Liste, calendrier et demandes dans le même onglet",
    gf4Title: "Tours partagés et exclusifs", gf4Desc: "Groupes, exclusif et location multi-jours",
    gf5Title: "Calendrier de disponibilité", gf5Desc: "Mensuel, hebdomadaire et journalier",
    gf6Title: "Paiements Stripe", gf6Desc: "Encaissements directs, acomptes et soldes séparés",
    gf7Title: "Récapitulatif quotidien", gf7Desc: "Encaissé et à encaisser chaque jour",
    gf8Title: "Acomptes et soldes", gf8Desc: "Paiements partiels et finaux",
    gf9Title: "Notifications en temps réel", gf9Desc: "Alertes push pour les nouvelles réservations",
    gf10Title: "Météo intégrée", gf10Desc: "Prévisions par port pour les 3 prochains jours",
    gf11Title: "Gestion du personnel", gf11Desc: "Invitez des collaborateurs avec les rôles Manager ou Staff",
    gf12Title: "Lien de réservation", gf12Desc: "Partagez sur WhatsApp, réseaux sociaux et Google",
    whyTitle: "Pourquoi choisir Nautiqo",
    whySubtitle: "Le contrôle total sur votre activité.",
    why1Title: "Vos clients restent les vôtres", why1Desc: "Aucun intermédiaire entre vous et vos clients",
    why2Title: "Encaissements sur votre compte", why2Desc: "Paiements directs via Stripe",
    why3Title: "Seulement 3% de commission", why3Desc: "Transparent. Vous gardez 97%.",
    why4Title: "Moins de travail manuel", why4Desc: "Automatisez réservations et confirmations",
    why5Title: "Tout sur une plateforme", why5Desc: "Fini Excel et les fichiers éparpillés",
    why6Title: "Pensé pour ceux qui naviguent", why6Desc: "Conçu pour les charters et tours nautiques",
    howLabel: "Comment ça marche",
    howTitle: "Trois étapes simples",
    how1Title: "Configurez votre flotte", how1Desc: "Ajoutez bateaux, tarifs et disponibilités.",
    how2Title: "Recevez des réservations", how2Desc: "Partagez le lien. Les clients réservent 24/7.",
    how3Title: "Gérez tout", how3Desc: "Confirmez, encaissez et suivez depuis un seul tableau de bord.",
    pricingLabel: "Abonnement",
    pricingTitle: "Un forfait. Tout inclus.",
    pricingSubtitle: "Essai gratuit de 30 jours. Choisissez ensuite la fréquence qui vous convient.",
    trialTitle: "Premier mois gratuit",
    trialSub: "Essai via App Store",
    trialPrice: "Gratuit",
    trialPer: "/ 30 jours",
    trialF1: "Toutes les fonctionnalités",
    trialF2: "Bateaux illimités",
    trialF3: "Activé depuis l'app, pas par carte",
    trialCta: "Créer un compte",
    monthlyTitle: "Mensuel",
    monthlySub: "Facturation mensuelle",
    monthlyPer: "/ mois",
    monthlyF1: "Tout illimité",
    monthlyF2: "Annulez quand vous voulez",
    monthlyF3: "Paiements, personnel, rapports",
    monthlyCta: "Choisir mensuel",
    annualBadge: "Économisez 17%",
    annualTitle: "Annuel",
    annualSub: "Facturation annuelle",
    annualPer: "/ an",
    annualNote: "16,58 €/mois — Économisez 40 € par an",
    annualF1: "Tout illimité",
    annualF2: "Mêmes fonctionnalités que le mensuel",
    annualF3: "Meilleur prix",
    annualCta: "Choisir annuel",
    pricingFooter: "Annulez quand vous voulez. Sans engagement, sans frais cachés.",
    downloadLabel: "Téléchargez l'app",
    downloadTitle: "Gérez tout depuis votre smartphone",
    downloadDesc: "Téléchargez Nautiqo et commencez à gérer réservations, disponibilités et paiements où que vous soyez.",
    downloadFor: "Télécharger pour",
    finalTitle1: "Commencez à gérer votre charter",
    finalTitle2: "sans intermédiaires",
    finalSubtitle: "Configurez Nautiqo en quelques minutes et prenez le contrôle de votre entreprise.",
    finalCtaPrimary: "Commencer gratuitement",
    finalWhatsapp: "Contactez-nous sur WhatsApp",
    faqLabel: "FAQ",
    faqTitle: "Questions fréquentes",
    footerDesc: "La plateforme pour gérer réservations, disponibilités et paiements de votre charter. Sans intermédiaires.",
    footerProduct: "Produit",
    footerAccount: "Compte",
    footerContacts: "Contact",
    footerCompany1: "est un service de",
    footerRights: "© 2026 Nautiqo. Tous droits réservés.",
    footerPrivacy: "Politique de confidentialité",
    footerTerms: "Conditions d'utilisation",
    faq1Q: "Puis-je utiliser Nautiqo sans site web ?",
    faq1A: "Oui. Vous recevez un lien de réservation dédié que vous pouvez partager sur les réseaux sociaux, WhatsApp, par email ou sur Google Business. Les clients réservent 24/7 sans besoin de site.",
    faq2Q: "Comment fonctionnent les paiements et les remboursements ?",
    faq2A: "Les paiements sont traités via Stripe Connect directement sur votre compte bancaire. Vous pouvez demander un acompte ou le paiement intégral. Les fonds arrivent en 2 à 7 jours ouvrés. Les remboursements peuvent être émis directement depuis l'app, en totalité ou en partie, et le client reçoit une notification email automatique.",
    faq3Q: "Puis-je gérer tours partagés, exclusifs et locations ?",
    faq3A: "Oui, vous pouvez activer plusieurs modes sur le même bateau : Tour en Groupe (prix par personne), Tour Exclusif (bateau entier) et Location (prix fixe journalier). Chacun a des tarifs et une saisonnalité indépendants.",
    faq4Q: "Les réservations multi-jours sont-elles prises en charge ?",
    faq4A: "Oui. Pour les bateaux en mode Location, vous pouvez activer des réservations de plusieurs jours avec skipper optionnel ou obligatoire. Le prix est calculé automatiquement selon la durée.",
    faq5Q: "Puis-je connecter mon site web ?",
    faq5A: "Absolument. Vous pouvez insérer votre lien de réservation sur n'importe quel site (WordPress, Wix, Squarespace), dans votre bio Instagram ou comme bouton WhatsApp. Installation en moins d'une minute, zéro installation technique.",
    faq6Q: "Puis-je gérer une équipe de collaborateurs ?",
    faq6A: "Oui. Vous pouvez inviter des collaborateurs par email et attribuer le rôle Manager (accès complet) ou Staff (lecture seule). Chacun se connecte avec ses propres identifiants.",
    faq7Q: "Puis-je synchroniser des calendriers externes ?",
    faq7A: "Oui, Nautiqo prend en charge la synchronisation iCal avec Google Calendar, Airbnb, Booking.com et d'autres systèmes compatibles avec le format standard.",
    faq8Q: "Comment fonctionne la période d'essai ?",
    faq8A: "Vous créez un compte gratuit sur le site, téléchargez l'app et activez votre abonnement directement depuis l'App Store. Le premier mois est gratuit — géré par Apple, sans prélèvement jusqu'à la fin de l'essai. Ensuite, vous choisissez mensuel ou annuel.",
  },
  de: {
    metaTitle: "Nautiqo — Verwalte deinen Charter ohne Vermittler",
    metaDesc: "Buchungen, Verfügbarkeit und Zahlungen für Boots-Charter. Nur 3% Provision. In 5 Minuten eingerichtet, 30 Tage kostenlos.",
    navWhy: "Warum Nautiqo",
    navFeatures: "Funktionen",
    navPricing: "Preise",
    navHow: "So funktioniert's",
    navFaq: "FAQ",
    navLogin: "Anmelden",
    navCta: "Kostenlos starten",
    heroTitle1: "Verwalte deinen Charter.",
    heroTitle2: "Ohne Vermittler.",
    heroSubtitle: "Buchungen, Verfügbarkeit und Zahlungen. Alles unter deiner Kontrolle.",
    heroCtaPrimary: "Kostenlos starten",
    heroCtaSecondary: "Zum Dashboard",
    heroBadge1: "Kostenlose Registrierung",
    heroBadge2: "In 5 Minuten eingerichtet",
    heroBadge3: "Erster Monat kostenlos über App Store",
    diffLabel: "Der Unterschied",
    diffTitle: "Warum Nautiqo anders ist",
    diffSubtitle: "Kein Marktplatz. Dein Verwaltungssystem.",
    diffMarketplace: "Marktplatz",
    diffFooterA: "Mit Nautiqo",
    diffFooterAStrong: "baust du ein Unternehmen auf",
    diffFooterB: "Mit anderen Plattformen",
    diffFooterBSpan: "arbeitest du für sie",
    compClienti: "Kunden",
    compClientiN: "Deine Kunden gehören dir",
    compClientiO: "Kunden gehören der Plattform",
    compCommissioni: "Provisionen",
    compCommissioniN: "3% transparent",
    compCommissioniO: "Bis zu 30%",
    compIncassi: "Einnahmen",
    compIncassiN: "Direkt auf dein Konto",
    compIncassiO: "Verzögert",
    compBranding: "Branding",
    compBrandingN: "Deine Marke",
    compBrandingO: "Ihre Marke",
    compFlotta: "Flottenverwaltung",
    compFlottaN: "Vollständig",
    compFlottaO: "Eingeschränkt",
    compMultiday: "Mehrtägig",
    compMultidayN: "Inklusive",
    compMultidayO: "Nicht verfügbar",
    compPrezzi: "Preise",
    compPrezziN: "Volle Kontrolle",
    compPrezziO: "Eingeschränkt",
    bookingsLabel: "Buchungen",
    bookingsTitle: "Jede Buchung im Griff",
    bookingsItem1: "Anfragen in Echtzeit mit Push-Benachrichtigungen",
    bookingsItem2: "Mit einem Klick bestätigen oder ablehnen",
    bookingsItem3: "Getrennte Verwaltung von Anzahlungen und Restzahlungen",
    bookingsItem4: "Liste, Kalender und Anfragen auf einem Bildschirm",
    availLabel: "Verfügbarkeit",
    availTitle: "Schluss mit Doppelbuchungen",
    availItem1: "Monats-, Wochen- und Tageskalender",
    availItem2: "iCal-Synchronisierung mit Google Calendar und Airbnb",
    availItem3: "Termine für Wartung blockieren",
    availItem4: "Verfügbarkeit immer in Echtzeit aktualisiert",
    fleetLabel: "Flotte",
    fleetTitle: "Deine Flotte, immer organisiert",
    fleetItem1: "Boote mit Fotos und Preisliste hinzufügen",
    fleetItem2: "Gruppentouren, exklusive Touren und mehrtägige Vermietung",
    fleetItem3: "Saisonpreise pro Person, halber oder ganzer Tag",
    fleetItem4: "Jedes Boot mit einem Klick aktivieren oder deaktivieren",
    gridLabel: "Funktionen",
    gridTitle: "Alles auf einer Plattform",
    gf1Title: "Flottenverwaltung", gf1Desc: "Alle Boote über ein einziges Dashboard",
    gf2Title: "Online-Buchungen 24/7", gf2Desc: "Eigener Link, immer aktiv",
    gf3Title: "Bestätigungen und Anfragen", gf3Desc: "Liste, Kalender und Anfragen im selben Tab",
    gf4Title: "Geteilte und exklusive Touren", gf4Desc: "Gruppen, exklusiv und mehrtägige Vermietung",
    gf5Title: "Verfügbarkeitskalender", gf5Desc: "Monatlich, wöchentlich und täglich",
    gf6Title: "Stripe-Zahlungen", gf6Desc: "Direkte Einnahmen, getrennte An- und Restzahlungen",
    gf7Title: "Tagesübersicht", gf7Desc: "Eingenommen und ausstehend an jedem Tag",
    gf8Title: "An- und Restzahlungen", gf8Desc: "Teil- und Schlusszahlungen",
    gf9Title: "Echtzeit-Benachrichtigungen", gf9Desc: "Push-Hinweise für neue Buchungen",
    gf10Title: "Integriertes Wetter", gf10Desc: "Vorhersagen pro Hafen für die nächsten 3 Tage",
    gf11Title: "Personalverwaltung", gf11Desc: "Mitarbeiter mit Manager- oder Staff-Rolle einladen",
    gf12Title: "Buchungslink", gf12Desc: "Teile über WhatsApp, Social Media und Google",
    whyTitle: "Warum Nautiqo wählen",
    whySubtitle: "Die volle Kontrolle über dein Geschäft.",
    why1Title: "Deine Kunden bleiben deine", why1Desc: "Kein Vermittler zwischen dir und deinen Kunden",
    why2Title: "Einnahmen auf dein Konto", why2Desc: "Direkte Zahlungen über Stripe",
    why3Title: "Nur 3% Provision", why3Desc: "Transparent. 97% bleiben bei dir.",
    why4Title: "Weniger manuelle Arbeit", why4Desc: "Automatisiere Buchungen und Bestätigungen",
    why5Title: "Alles auf einer Plattform", why5Desc: "Kein Excel und keine verstreuten Tabellen mehr",
    why6Title: "Für alle, die zur See fahren", why6Desc: "Entwickelt für Charter und Bootstouren",
    howLabel: "So funktioniert's",
    howTitle: "Drei einfache Schritte",
    how1Title: "Flotte einrichten", how1Desc: "Boote, Preise und Verfügbarkeit hinzufügen.",
    how2Title: "Buchungen erhalten", how2Desc: "Teile den Link. Kunden buchen 24/7.",
    how3Title: "Alles verwalten", how3Desc: "Bestätige, kassiere und überwache über ein Dashboard.",
    pricingLabel: "Abonnement",
    pricingTitle: "Ein Tarif. Alles inklusive.",
    pricingSubtitle: "30 Tage kostenlos testen. Dann den passenden Abrechnungszeitraum wählen.",
    trialTitle: "Erster Monat kostenlos",
    trialSub: "Testphase über App Store",
    trialPrice: "Kostenlos",
    trialPer: "/ 30 Tage",
    trialF1: "Alle Funktionen",
    trialF2: "Unbegrenzte Boote",
    trialF3: "Über die App aktiviert, nicht über die Karte",
    trialCta: "Konto erstellen",
    monthlyTitle: "Monatlich",
    monthlySub: "Monatliche Abrechnung",
    monthlyPer: "/ Monat",
    monthlyF1: "Alles unbegrenzt",
    monthlyF2: "Jederzeit kündbar",
    monthlyF3: "Zahlungen, Personal, Berichte",
    monthlyCta: "Monatlich wählen",
    annualBadge: "17% sparen",
    annualTitle: "Jährlich",
    annualSub: "Jährliche Abrechnung",
    annualPer: "/ Jahr",
    annualNote: "16,58 €/Monat — Spare 40 € pro Jahr",
    annualF1: "Alles unbegrenzt",
    annualF2: "Gleiche Funktionen wie monatlich",
    annualF3: "Bester Preis",
    annualCta: "Jährlich wählen",
    pricingFooter: "Jederzeit kündbar. Keine Bindung, keine versteckten Kosten.",
    downloadLabel: "App herunterladen",
    downloadTitle: "Verwalte alles von deinem Smartphone",
    downloadDesc: "Lade Nautiqo herunter und verwalte Buchungen, Verfügbarkeit und Zahlungen, wo immer du bist.",
    downloadFor: "Herunterladen für",
    finalTitle1: "Verwalte deinen Charter",
    finalTitle2: "ohne Vermittler",
    finalSubtitle: "Richte Nautiqo in wenigen Minuten ein und übernimm die Kontrolle über dein Geschäft.",
    finalCtaPrimary: "Kostenlos starten",
    finalWhatsapp: "Kontaktiere uns auf WhatsApp",
    faqLabel: "FAQ",
    faqTitle: "Häufige Fragen",
    footerDesc: "Die Plattform zur Verwaltung von Buchungen, Verfügbarkeit und Zahlungen deines Charters. Ohne Vermittler.",
    footerProduct: "Produkt",
    footerAccount: "Konto",
    footerContacts: "Kontakt",
    footerCompany1: "ist ein Service von",
    footerRights: "© 2026 Nautiqo. Alle Rechte vorbehalten.",
    footerPrivacy: "Datenschutz",
    footerTerms: "Nutzungsbedingungen",
    faq1Q: "Kann ich Nautiqo ohne Website nutzen?",
    faq1A: "Ja. Du erhältst einen eigenen Buchungslink, den du auf Social Media, WhatsApp, per E-Mail oder über Google Business teilen kannst. Kunden buchen 24/7 ganz ohne Website.",
    faq2Q: "Wie funktionieren Zahlungen und Rückerstattungen?",
    faq2A: "Zahlungen werden über Stripe Connect direkt auf dein Bankkonto abgewickelt. Du kannst eine Anzahlung oder die volle Zahlung anfordern. Die Beträge treffen in 2-7 Werktagen ein. Rückerstattungen kannst du direkt in der App vornehmen, ganz oder teilweise, und der Kunde erhält automatisch eine E-Mail-Benachrichtigung.",
    faq3Q: "Kann ich geteilte Touren, exklusive Touren und Vermietungen verwalten?",
    faq3A: "Ja, du kannst auf demselben Boot mehrere Modi aktivieren: Gruppentour (Preis pro Person), Exklusivtour (ganzes Boot) und Vermietung (fester Tagespreis). Jeder Modus hat unabhängige Preise und Saisonzeiten.",
    faq4Q: "Werden mehrtägige Buchungen unterstützt?",
    faq4A: "Ja. Für Boote im Vermietungsmodus kannst du mehrtägige Buchungen mit optionalem oder verpflichtendem Skipper aktivieren. Der Preis wird automatisch anhand der Dauer berechnet.",
    faq5Q: "Kann ich meine Website verbinden?",
    faq5A: "Auf jeden Fall. Du kannst deinen Buchungslink auf jeder Website (WordPress, Wix, Squarespace), in deiner Instagram-Bio oder als WhatsApp-Button einbinden. In unter einer Minute eingerichtet, ohne Installation.",
    faq6Q: "Kann ich ein Team von Mitarbeitern verwalten?",
    faq6A: "Ja. Du kannst Mitarbeiter per E-Mail einladen und ihnen die Rolle Manager (voller Zugriff) oder Staff (nur Ansicht) zuweisen. Jeder meldet sich mit eigenen Zugangsdaten an.",
    faq7Q: "Kann ich externe Kalender synchronisieren?",
    faq7A: "Ja, Nautiqo unterstützt die iCal-Synchronisierung mit Google Calendar, Airbnb, Booking.com und anderen Systemen, die das Standardformat unterstützen.",
    faq8Q: "Wie funktioniert die Testphase?",
    faq8A: "Du erstellst ein kostenloses Konto auf der Website, lädst die App herunter und aktivierst dein Abo direkt über den App Store. Der erste Monat ist kostenlos — von Apple verwaltet, ohne Abbuchung bis zum Ende der Testphase. Danach wählst du monatlich oder jährlich.",
  },
};

const LOGO_URL = "https://nautiqo.cloud/logo-nautiqo.png";
const HERO_BG = "https://nautiqo.cloud/placeholder.jpg";
const DARK_BG = "https://nautiqo.cloud/placeholder.jpg";

const SCREENS = {
  dashboard: "https://nautiqo.cloud/screen1.png",
  bookings: "https://nautiqo.cloud/screen2.png",
  calendar: "https://nautiqo.cloud/screen3.png",
  fleet: "https://nautiqo.cloud/screen4.png",
};

const heading = { fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif" };
const body = { fontFamily: "'Manrope', sans-serif" };

const PhoneMockup = ({ src, alt, className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="relative bg-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-slate-900/30">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-slate-900 rounded-b-2xl z-10" />
      <div className="relative bg-white rounded-[2.5rem] overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-8 bg-white z-[5] rounded-t-[2.5rem]" />
        <img src={src} alt={alt} className="w-full h-auto" loading="lazy" />
      </div>
    </div>
  </div>
);

const buildFaqs = (t) => [
  { q: t.faq1Q, a: t.faq1A },
  { q: t.faq2Q, a: t.faq2A },
  { q: t.faq3Q, a: t.faq3A },
  { q: t.faq4Q, a: t.faq4A },
  { q: t.faq5Q, a: t.faq5A },
  { q: t.faq6Q, a: t.faq6A },
  { q: t.faq7Q, a: t.faq7A },
  { q: t.faq8Q, a: t.faq8A },
];

const FAQItem = ({ q, a, isOpen, onClick }) => (
  <div className="border-b border-slate-200/60 last:border-0">
    <button onClick={onClick} className="w-full py-5 flex items-center justify-between text-left group">
      <span className="font-medium text-slate-900 pr-8 group-hover:text-blue-600 transition-colors">{q}</span>
      <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-600' : ''}`} />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-48 pb-5' : 'max-h-0'}`}>
      <p className="text-slate-500 leading-relaxed">{a}</p>
    </div>
  </div>
);

const buildComparisonRows = (t) => [
  { label: t.compClienti, nautiqo: t.compClientiN, other: t.compClientiO },
  { label: t.compCommissioni, nautiqo: t.compCommissioniN, other: t.compCommissioniO },
  { label: t.compIncassi, nautiqo: t.compIncassiN, other: t.compIncassiO },
  { label: t.compBranding, nautiqo: t.compBrandingN, other: t.compBrandingO },
  { label: t.compFlotta, nautiqo: t.compFlottaN, other: t.compFlottaO },
  { label: t.compMultiday, nautiqo: t.compMultidayN, other: t.compMultidayO },
  { label: t.compPrezzi, nautiqo: t.compPrezziN, other: t.compPrezziO },
];

const trackEvent = (eventName, params = {}) => {
  if (window.gtag) window.gtag('event', eventName, params);
};

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const t = usePageT(translations);
  const faqs = buildFaqs(t);
  const comparisonRows = buildComparisonRows(t);
  usePageMeta({
    title: t.metaTitle,
    description: t.metaDesc,
  });

  return (
    <div className="min-h-screen bg-white" style={body}>
      <CustomCursor />
      <StickyHelpButton />

      {/* ─── NAV ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-18">
            <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
              <div className="w-9 h-9 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
              <span className="text-slate-900 font-bold text-lg tracking-tight" style={heading}>Nautiqo</span>
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              <a href="#differenza" className="text-slate-500 hover:text-slate-900 text-sm font-medium transition-colors">{t.navWhy}</a>
              <a href="#features" className="text-slate-500 hover:text-slate-900 text-sm font-medium transition-colors">{t.navFeatures}</a>
              <a href="#prezzi" className="text-slate-500 hover:text-slate-900 text-sm font-medium transition-colors">{t.navPricing}</a>
              <a href="#come-funziona" className="text-slate-500 hover:text-slate-900 text-sm font-medium transition-colors">{t.navHow}</a>
              <a href="#faq" className="text-slate-500 hover:text-slate-900 text-sm font-medium transition-colors">{t.navFaq}</a>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <Link to="/login" className="text-slate-600 hover:text-slate-900 text-sm font-medium px-3 py-2 transition-colors">
                {t.navLogin}
              </Link>
              <a href="/register" onClick={() => trackEvent('click_register', { location: 'nav' })} className="inline-flex items-center px-5 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-full hover:bg-slate-800 transition-all" data-testid="nav-cta">
                {t.navCta}
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── 1. HERO ─── */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-slate-900/10" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 w-full">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeInUp delay={0}>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6 tracking-tighter" style={heading}>
                  {t.heroTitle1}{" "}
                  <span className="text-blue-400">{t.heroTitle2}</span>
                </h1>
              </FadeInUp>

              <FadeInUp delay={120}>
                <p className="text-lg sm:text-xl text-white/65 leading-relaxed mb-8 max-w-xl">
                  {t.heroSubtitle}
                </p>
              </FadeInUp>

              <FadeInUp delay={220}>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <a href="/register" onClick={() => trackEvent('click_register', { location: 'hero' })} className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 font-semibold rounded-full hover:bg-slate-50 transition-all shadow-xl hover:-translate-y-0.5" data-testid="hero-cta-primary">
                    {t.heroCtaPrimary}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                  <Link to="/login" className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur text-white font-semibold rounded-full border border-white/20 hover:bg-white/20 transition-all" data-testid="hero-cta-secondary">
                    {t.heroCtaSecondary}
                  </Link>
                </div>
              </FadeInUp>

              <FadeInUp delay={320}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/55">
                  <span className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" /> {t.heroBadge1}
                  </span>
                  <span className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" /> {t.heroBadge2}
                  </span>
                  <span className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" /> {t.heroBadge3}
                  </span>
                </div>
              </FadeInUp>
            </div>

            {/* Commission Calculator - replaces static phone mockup */}
            <FadeInUp delay={400} className="lg:col-span-5">
              <CommissionCalculator />
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* ─── 2. DIFFERENTIATION ─── */}
      <section className="py-24 md:py-32 bg-white" id="differenza" data-testid="differentiation-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-blue-600 mb-4 block">{t.diffLabel}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tighter mb-4" style={heading}>
              {t.diffTitle}
            </h2>
            <p className="text-lg text-slate-500">
              {t.diffSubtitle}
            </p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="comparison-table">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-4 px-4 text-sm font-medium text-slate-400 w-1/3"></th>
                  <th className="text-left py-4 px-4 w-1/3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="" className="w-full h-full scale-[1.06]" /></div>
                      <span className="font-bold text-slate-900" style={heading}>Nautiqo</span>
                    </div>
                  </th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-slate-400 w-1/3">{t.diffMarketplace}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-5 px-4 text-sm font-semibold text-slate-900">{row.label}</td>
                    <td className="py-5 px-4">
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-900">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        {row.nautiqo}
                      </span>
                    </td>
                    <td className="py-5 px-4">
                      <span className="inline-flex items-center gap-2 text-sm text-slate-400">
                        <X className="w-4 h-4 text-red-400 flex-shrink-0" />
                        {row.other}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center mt-12 text-lg text-slate-600">
            {t.diffFooterA} <strong className="text-slate-900">{t.diffFooterAStrong}</strong>.<br />
            {t.diffFooterB} <span className="text-slate-400">{t.diffFooterBSpan}</span>.
          </p>
        </div>
      </section>

      {/* ─── 3. FEATURES SHOWCASE ─── */}
      {/* Prenotazioni */}
      <section className="py-24 md:py-32 bg-slate-50/60" id="features" data-testid="feature-bookings">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-emerald-50 to-teal-50/50 rounded-[3rem] -z-10" />
                <PhoneMockup src={SCREENS.bookings} alt="Prenotazioni" className="max-w-xs mx-auto" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-600 mb-4 block">{t.bookingsLabel}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tighter mb-6" style={heading}>
                {t.bookingsTitle}
              </h2>
              <ul className="space-y-3">
                {[t.bookingsItem1, t.bookingsItem2, t.bookingsItem3, t.bookingsItem4].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </div>
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Disponibilita */}
      <section className="py-24 md:py-32" data-testid="feature-calendar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-bold text-violet-600 mb-4 block">{t.availLabel}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tighter mb-6" style={heading}>
                {t.availTitle}
              </h2>
              <ul className="space-y-3">
                {[t.availItem1, t.availItem2, t.availItem3, t.availItem4].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-violet-600" />
                    </div>
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-violet-50 to-purple-50/50 rounded-[3rem] -z-10" />
                <PhoneMockup src={SCREENS.calendar} alt="Calendario" className="max-w-xs mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flotta */}
      <section className="py-24 md:py-32 bg-slate-50/60" data-testid="feature-fleet">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-amber-50 to-orange-50/50 rounded-[3rem] -z-10" />
                <PhoneMockup src={SCREENS.fleet} alt="Flotta" className="max-w-xs mx-auto" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="uppercase tracking-[0.2em] text-xs font-bold text-amber-600 mb-4 block">{t.fleetLabel}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tighter mb-6" style={heading}>
                {t.fleetTitle}
              </h2>
              <ul className="space-y-3">
                {[t.fleetItem1, t.fleetItem2, t.fleetItem3, t.fleetItem4].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-amber-600" />
                    </div>
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 5. GRID FEATURES ─── */}
      <section className="py-24 md:py-32 bg-slate-50/60" data-testid="grid-features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-slate-400 mb-4 block">{t.gridLabel}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tighter" style={heading}>
              {t.gridTitle}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Ship, title: t.gf1Title, desc: t.gf1Desc },
              { icon: Globe, title: t.gf2Title, desc: t.gf2Desc },
              { icon: CalendarCheck, title: t.gf3Title, desc: t.gf3Desc },
              { icon: Users, title: t.gf4Title, desc: t.gf4Desc },
              { icon: Calendar, title: t.gf5Title, desc: t.gf5Desc },
              { icon: CreditCard, title: t.gf6Title, desc: t.gf6Desc },
              { icon: BarChart3, title: t.gf7Title, desc: t.gf7Desc },
              { icon: Wallet, title: t.gf8Title, desc: t.gf8Desc },
              { icon: Bell, title: t.gf9Title, desc: t.gf9Desc },
              { icon: Clock, title: t.gf10Title, desc: t.gf10Desc },
              { icon: UserCheck, title: t.gf11Title, desc: t.gf11Desc },
              { icon: Share2, title: t.gf12Title, desc: t.gf12Desc },
            ].map((f, i) => (
              <FadeInUp key={i} delay={i * 60} className="group p-5 rounded-2xl bg-white border border-slate-100 hover:border-blue-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`grid-feature-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-50 group-hover:bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                    <f.icon className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-0.5 text-sm">{f.title}</h3>
                    <p className="text-sm text-slate-400">{f.desc}</p>
                  </div>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 6. WHY NAUTIQO (DARK) ─── */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="why-nautiqo-section">
        <img src={DARK_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tighter mb-4" style={heading}>
              {t.whyTitle}
            </h2>
            <p className="text-slate-400">
              {t.whySubtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Users, title: t.why1Title, desc: t.why1Desc },
              { icon: CreditCard, title: t.why2Title, desc: t.why2Desc },
              { icon: BarChart3, title: t.why3Title, desc: t.why3Desc },
              { icon: Zap, title: t.why4Title, desc: t.why4Desc },
              { icon: Shield, title: t.why5Title, desc: t.why5Desc },
              { icon: Anchor, title: t.why6Title, desc: t.why6Desc },
            ].map((item, i) => (
              <FadeInUp key={i} delay={i * 80} className="p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-blue-400/30 hover:-translate-y-1 transition-all duration-300" data-testid={`why-card-${i}`}>
                <div className="w-10 h-10 bg-blue-500/15 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 7. HOW IT WORKS ─── */}
      <section className="py-24 md:py-32 bg-slate-900" id="come-funziona" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-blue-400 mb-4 block">{t.howLabel}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tighter" style={heading}>
              {t.howTitle}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { step: "01", title: t.how1Title, desc: t.how1Desc, color: "text-blue-500" },
              { step: "02", title: t.how2Title, desc: t.how2Desc, color: "text-emerald-500" },
              { step: "03", title: t.how3Title, desc: t.how3Desc, color: "text-violet-500" },
            ].map((item, i) => (
              <div key={i} className="text-center p-8 bg-white/5 rounded-2xl border border-white/10" data-testid={`step-${item.step}`}>
                <span className={`text-7xl font-bold ${item.color} block mb-4 tracking-tighter`} style={heading}>{item.step}</span>
                <h3 className="text-lg font-bold text-white mb-2" style={heading}>{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className="py-24 md:py-32 bg-white" id="prezzi" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-blue-600 mb-4 block">{t.pricingLabel}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tighter mb-4" style={heading}>
              {t.pricingTitle}
            </h2>
            <p className="text-lg text-slate-500">
              {t.pricingSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Trial */}
            <div className="rounded-2xl border border-slate-200 p-7 bg-white" data-testid="plan-trial">
              <h3 className="text-lg font-bold text-slate-900 mb-1" style={heading}>{t.trialTitle}</h3>
              <p className="text-sm text-slate-500 mb-4">{t.trialSub}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900 tracking-tight" style={heading}>{t.trialPrice}</span>
                <span className="text-slate-400 text-sm ml-1">{t.trialPer}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {[t.trialF1, t.trialF2, t.trialF3].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <a href="/register" onClick={() => trackEvent('click_register', { location: 'pricing_trial' })} className="block text-center w-full px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-full hover:border-slate-300 hover:bg-slate-50 transition-all text-sm">
                {t.trialCta}
              </a>
            </div>

            {/* Mensile */}
            <div className="rounded-2xl border border-slate-200 p-7 bg-white" data-testid="plan-monthly">
              <h3 className="text-lg font-bold text-slate-900 mb-1" style={heading}>{t.monthlyTitle}</h3>
              <p className="text-sm text-slate-500 mb-4">{t.monthlySub}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900 tracking-tight" style={heading}>€19,90</span>
                <span className="text-slate-400 text-sm ml-1">{t.monthlyPer}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {[t.monthlyF1, t.monthlyF2, t.monthlyF3].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <a href="/pricing" onClick={() => trackEvent('click_pricing', { plan: 'monthly' })} className="block text-center w-full px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-full hover:border-slate-300 hover:bg-slate-50 transition-all text-sm">
                {t.monthlyCta}
              </a>
            </div>

            {/* Annuale */}
            <div className="rounded-2xl border-2 border-blue-600 p-7 bg-slate-900 text-white relative shadow-lg shadow-blue-500/10" data-testid="plan-annual">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="bg-emerald-500 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">{t.annualBadge}</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-1" style={heading}>{t.annualTitle}</h3>
              <p className="text-sm text-slate-400 mb-4">{t.annualSub}</p>
              <div className="mb-2">
                <span className="text-4xl font-bold text-white tracking-tight" style={heading}>€199</span>
                <span className="text-slate-400 text-sm ml-1">{t.annualPer}</span>
              </div>
              <p className="text-xs text-emerald-400 font-medium mb-4">{t.annualNote}</p>
              <ul className="space-y-2 mb-6">
                {[t.annualF1, t.annualF2, t.annualF3].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <a href="/pricing" onClick={() => trackEvent('click_pricing', { plan: 'annual' })} className="block text-center w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition-all text-sm shadow-lg shadow-blue-600/25">
                {t.annualCta}
              </a>
            </div>
          </div>

          <p className="text-center text-sm text-slate-400 mt-8">
            {t.pricingFooter}
          </p>
        </div>
      </section>

      {/* ─── DOWNLOAD APP ─── */}
      <section className="py-24 md:py-32" id="download" data-testid="download-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-bold text-blue-600 mb-4 block">{t.downloadLabel}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tighter mb-6" style={heading}>
                {t.downloadTitle}
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                {t.downloadDesc}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://apps.apple.com/it/app/nautiqo/id6762418993" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('click_download', { platform: 'ios' })} className="inline-flex items-center gap-3 px-6 py-3.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors" data-testid="appstore-btn">
                  <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 21.99 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 21.99C7.79 22.03 6.8 20.68 5.96 19.47C4.25 16.97 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/></svg>
                  <div>
                    <span className="text-xs text-white/60 block">{t.downloadFor}</span>
                    <span className="text-base font-semibold">iOS</span>
                  </div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.nautiqo.app" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('click_download', { platform: 'android' })} className="inline-flex items-center gap-3 px-6 py-3.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors" data-testid="playstore-btn">
                  <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.77C2.94 23.36 2.8 22.87 2.8 22.3V1.7C2.8 1.13 2.94 .64 3.18 .23L13.04 12L3.18 23.77ZM4.44 24.5L14.84 13.83L17.62 16.7L5.55 24.5H4.44ZM17.62 7.3L14.84 10.17L4.44-.5H5.55L17.62 7.3ZM18.86 8.13L21.42 9.6C22.67 10.3 22.67 13.7 21.42 14.4L18.86 15.87L15.67 12L18.86 8.13Z"/></svg>
                  <div>
                    <span className="text-xs text-white/60 block">{t.downloadFor}</span>
                    <span className="text-base font-semibold">Android</span>
                  </div>
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-blue-50 to-indigo-50/50 rounded-[3rem] -z-10" />
                <PhoneMockup src={SCREENS.dashboard} alt="Nautiqo App" className="max-w-xs" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 8. FINAL CTA ─── */}
      <section className="py-24 md:py-32" id="cta" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-slate-900 rounded-[2.5rem] p-10 lg:p-20 text-center text-white overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold mb-5 tracking-tighter" style={heading}>
                {t.finalTitle1}<br className="hidden sm:block" /> {t.finalTitle2}
              </h2>
              <p className="text-lg text-slate-400 max-w-xl mx-auto mb-10">
                {t.finalSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/register" onClick={() => trackEvent('click_register', { location: 'final_cta' })} className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 font-semibold rounded-full hover:bg-slate-50 transition-all shadow-xl" data-testid="final-cta-primary">
                  {t.finalCtaPrimary}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <a href="https://wa.me/393793782759?text=Ciao!%20Vorrei%20saperne%20di%20pi%C3%B9%20su%20Nautiqo%20%F0%9F%9A%A4" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('click_whatsapp', { location: 'final_cta' })} className="inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-[#25D366]/15 text-[#25D366] font-semibold rounded-full border border-[#25D366]/30 hover:bg-[#25D366]/25 transition-all" data-testid="final-cta-whatsapp">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current flex-shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.552 4.103 1.513 5.834L0 24l6.335-1.483A11.944 11.944 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.8 9.8 0 01-4.988-1.364l-.358-.213-3.76.879.894-3.665-.233-.375A9.816 9.816 0 012.182 12c0-5.418 4.4-9.818 9.818-9.818 5.418 0 9.818 4.4 9.818 9.818 0 5.418-4.4 9.818-9.818 9.818z"/></svg>
                  {t.finalWhatsapp}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-24 md:py-32 bg-slate-50/60" id="faq" data-testid="faq-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-slate-400 mb-4 block">{t.faqLabel}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tighter" style={heading}>
              {t.faqTitle}
            </h2>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 px-6 sm:px-8">
            {faqs.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} isOpen={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-slate-900 text-white py-16 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
                <span className="text-white font-bold text-lg" style={heading}>Nautiqo</span>
              </div>
              <p className="text-slate-400 text-sm max-w-sm leading-relaxed">
                {t.footerDesc}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-4">{t.footerProduct}</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href="#differenza" className="hover:text-white transition-colors">{t.navWhy}</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">{t.navFeatures}</a></li>
                <li><a href="#prezzi" className="hover:text-white transition-colors">{t.navPricing}</a></li>
                <li><a href="#come-funziona" className="hover:text-white transition-colors">{t.navHow}</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">{t.navFaq}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-4">{t.footerAccount}</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><Link to="/login" className="hover:text-white transition-colors">{t.navLogin}</Link></li>
                <li><a href="mailto:info@nautiqo.cloud" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="contact-email-link">{t.footerContacts}</a></li>
                <li><span className="text-slate-500 select-all">info@nautiqo.cloud</span></li>
                <li><a href="https://wa.me/393793782759" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 transition-colors select-all flex items-center gap-1.5">
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current flex-shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.552 4.103 1.513 5.834L0 24l6.335-1.483A11.944 11.944 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.8 9.8 0 01-4.988-1.364l-.358-.213-3.76.879.894-3.665-.233-.375A9.816 9.816 0 012.182 12c0-5.418 4.4-9.818 9.818-9.818 5.418 0 9.818 4.4 9.818 9.818 0 5.418-4.4 9.818-9.818 9.818z"/></svg>
                  +39 379 378 2759
                </a></li>
              </ul>
            </div>
          </div>

          {/* Company details */}
          <div className="border-t border-slate-800 pt-8 mb-6" data-testid="company-details">
            <p className="text-slate-500 text-xs leading-relaxed max-w-3xl">
              <span className="font-semibold text-slate-400">Nautiqo</span> {t.footerCompany1} <span className="text-slate-300">Seawave S.a.s. di Laudicina Marco &amp; C.</span><br />
              P.IVA <span className="select-all">02855570814</span>
              <span className="hidden sm:inline"> · </span>
              <span className="block sm:inline">Email: <a href="mailto:info@nautiqo.cloud" className="hover:text-white transition-colors select-all">info@nautiqo.cloud</a></span>
            </p>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-xs">{t.footerRights}</p>
            <div className="flex items-center gap-6 text-xs text-slate-500">
              <a href="/privacy" className="hover:text-white transition-colors">{t.footerPrivacy}</a>
              <a href="#" className="hover:text-white transition-colors">{t.footerTerms}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
